import { normalizePatientId } from '../../../utils/patientAccess';

/** The modal's tabs in their original order; the wizard walks them in this order. */
export const STEP_KEYS = ['informations', 'todoList', 'visitForm', 'stock', 'shipments', 'expenses', 'visitEvaluation', 'craApproval'];

export const visibleStepKeys = (showCraApproval) =>
  STEP_KEYS.filter((key) => key !== 'craApproval' || showCraApproval);

/*
 * Required checks per step. They MIRROR handleCompleteVisit in VisitModal.js
 * (same fields, same truthiness, same conditions) so the progress shown never
 * disagrees with what "Ziyareti Tamamla" accepts. Change both together.
 * `key` names the label in visitModal.fields; `section` is the block the field
 * sits in, so a section header can show its own missing count.
 */
const check = (key, value, section) => ({ key, ok: Boolean(value), section });

const visitFormChecks = (formData) => [
  check('visitType', formData.visitType, 'type'),
  check('sourceDocument', formData.sourceDocument, 'type'),
  check('date', formData.date, 'time'),
  check('startTime', formData.startTime, 'time'),
  check('endTime', formData.endTime, 'time'),
  check('patientId', normalizePatientId(formData.patientId), 'patient'),
  check('doctor', formData.doctor, 'patient'),
  check('hospital', formData.hospital, 'location'),
  check('city', formData.city, 'location'),
  check('travelType', formData.travelType, 'location'),
];

// At least one to-do item ticked (stockEntryRequired counts too, as in the handler)
const todoChecks = (formData) => [
  check('anyTodo', Object.values(formData.todoList || {}).some((value) => value === true)),
];

// "Stok girişi yapılacak" ticked → at least one stock line before completing
const stockChecks = (formData, stockItems) =>
  formData.todoList?.stockEntryRequired ? [check('stockItem', stockItems.length > 0)] : [];

const homeEvaluationChecks = (formData, evalData) => {
  const checks = [
    check('windowOpenDate', evalData.windowOpenDate, 'B'),
    check('targetDate', evalData.targetDate, 'B'),
    check('windowCloseDate', evalData.windowCloseDate, 'B'),
    check('clinicianFullName', formData.clinicianFullName, 'C'),
    check('travelBefore', evalData.travelBeforeHours || evalData.travelBeforeMinutes, 'D'),
    check('travelDistanceBefore', evalData.travelDistanceBefore, 'D'),
    check('visitStartDate', formData.visitStartDate, 'E'),
    check('visitStartTime', formData.visitStartTime, 'E'),
    check('visitEndDate', formData.visitEndDate, 'E'),
    check('visitEndTime', formData.visitEndTime, 'E'),
    check('visitDuration', evalData.visitDurationHours || evalData.visitDurationMinutes, 'E'),
    check('travelAfter', evalData.travelAfterHours || evalData.travelAfterMinutes, 'F'),
    check('issueOccurred', evalData.issueOccurred, 'G'),
  ];
  if (evalData.issueOccurred === 'yes') {
    checks.push(
      check('issueResultedInMiss', evalData.issueResultedInMiss, 'G'),
      check('issueSource', evalData.issueCategory, 'G')
    );
  }
  checks.push(
    check('dateInitialSourceReceived', evalData.dateInitialSourceReceived, 'I'),
    check('dateSourceFinalized', evalData.dateSourceFinalized, 'I'),
    check('wasAeSaeTaken', evalData.wasAeSaeTaken, 'J'),
    check('dcfCreated', evalData.dcfCreated, 'J')
  );
  return checks;
};

// Every non-home type uses the hospital (site) evaluation, as in the handler.
// D — Tamamlanma Tarihleri deliberately has no checks: it never blocks completion.
const siteEvaluationChecks = (evalData) => {
  const checks = [
    check('screenFail', evalData.screenFail, 'B'),
    check('dropPatient', evalData.dropPatient, 'B'),
    check('protocolDeviation', evalData.protocolDeviation, 'C'),
  ];
  if (evalData.protocolDeviation === 'yes') checks.push(check('capaOpened', evalData.capaOpened, 'C'));
  checks.push(
    check('processEndDate', evalData.processEndDate, 'E'),
    check('slaTarget', evalData.slaTarget, 'E'),
    check('issueOccurred', evalData.issueOccurred, 'F')
  );
  if (evalData.issueOccurred === 'yes') {
    checks.push(check('issueCategory', evalData.issueCategory, 'F'), check('issueDetails', evalData.issueDetails, 'F'));
  }
  return checks;
};

const evaluationChecks = (formData) => {
  const evalData = formData.visitEvaluation || {};
  return formData.visitType === 'home' ? homeEvaluationChecks(formData, evalData) : siteEvaluationChecks(evalData);
};

/**
 * Wizard state of every visible step:
 * - `info`: nothing to fill in (Bilgiler)
 * - `optional`: no required field (Kargo, Harcamalar, Stok unless required)
 * - `missing` / `done`: has required fields, some / none empty
 * plus the overall share of required fields that are filled in.
 */
export const buildVisitProgress = ({ formData, stockItems, shipments, expenses, showCraApproval }) => {
  const checksByStep = {
    informations: [],
    todoList: todoChecks(formData),
    visitForm: visitFormChecks(formData),
    stock: stockChecks(formData, stockItems),
    shipments: [],
    expenses: [],
    visitEvaluation: evaluationChecks(formData),
    craApproval: [],
  };
  const recordsByStep = {
    stock: stockItems.length,
    shipments: shipments.length,
    expenses: expenses.length,
  };

  const steps = visibleStepKeys(showCraApproval).map((key) => {
    const checks = checksByStep[key];
    const missing = checks.filter((item) => !item.ok);
    let state = 'optional';
    if (key === 'informations') state = 'info';
    else if (checks.length > 0) state = missing.length > 0 ? 'missing' : 'done';
    return { key, checks, missing, state, records: recordsByStep[key] ?? null };
  });

  const total = steps.reduce((sum, step) => sum + step.checks.length, 0);
  const missingCount = steps.reduce((sum, step) => sum + step.missing.length, 0);
  return {
    steps,
    total,
    missingCount,
    percent: total > 0 ? Math.round(((total - missingCount) / total) * 100) : 100,
    ready: missingCount === 0,
  };
};

/**
 * Last day of the visit window and the completion deadline (last day + 72 h).
 * Same arithmetic as the past-visit check in VisitModal's load effect.
 */
export const visitCompletionWindow = (visit) => {
  if (!visit?.firstVisitDate || !visit?.period) return null;
  const firstDate = new Date(visit.firstVisitDate);
  firstDate.setHours(0, 0, 0, 0);
  const lastDate = new Date(firstDate);
  if (visit.period >= 0) lastDate.setDate(firstDate.getDate() + visit.period - 1);
  lastDate.setHours(23, 59, 59, 999);
  const completionDeadline = new Date(lastDate);
  completionDeadline.setHours(completionDeadline.getHours() + 72);
  return { lastDate, completionDeadline };
};

/** Stored visit type values, in the order the form offers them. */
export const VISIT_TYPES = ['hastane', 'home', 'telefon', 'unplanning', 'handover'];
